// ---------------------------------------------------------------------------
// Régua de Cobrança — constantes de domínio (handoff 0090).
//
// Lógica SEMPRE por `stage_type`/enums — nunca pelo `name` da raia, que é
// editável pelo usuário. Microcópia copiada literalmente do protótipo
// (docs/design/design_handoff_cobrancas_vencidas/) — foi escrita com o time de
// cobrança e comunica regra de negócio.
// ---------------------------------------------------------------------------

export const CHANNELS = [
   { value: 'PHONE', label: 'Telefone' },
   { value: 'WHATSAPP', label: 'WhatsApp' },
   { value: 'EMAIL', label: 'E-mail' },
   { value: 'IN_PERSON', label: 'Presencial' },
   { value: 'OTHER', label: 'Outro' },
];

export const CHANNEL_LABELS = CHANNELS.reduce(
   (acc, channel) => ({ ...acc, [channel.value]: channel.label }),
   {}
);

// Rótulos pt-BR das actions da timeline C4 (§4.3 do contrato).
//
// `send_external` continua aqui como rótulo HISTÓRICO: o endpoint morreu no
// 0103, mas os eventos gravados antes dele seguem no banco e precisam de nome.
export const ACTION_LABELS = {
   create: 'Caso criado',
   update: 'Caso atualizado',
   move_stage: 'Raia alterada',
   settle: 'Caso recebido',
   cancel: 'Caso cancelado',
   send_external: 'Enviado para cobrança administrativa',
   reopen: 'Caso reaberto',
   charge_added: 'Título adicionado',
   charge_settled: 'Título baixado',
   charge_cancelled: 'Título cancelado',
   charge_reopened: 'Título reaberto',
   // Trilha e acerto (handoff 0103 — 2026-08-28).
   escalate: 'Títulos enviados ao escritório',
   return: 'Títulos devolvidos à régua interna',
   check: 'Título conferido',
   uncheck: 'Conferência desfeita',
};

// Título específico do action `update` por campo alterado (feedback do que
// foi mexido — decisão 2026-08-17). Campos são os do PATCH §4.4. Fallback:
// ACTION_LABELS.update.
export const UPDATE_FIELD_TITLES = {
   next_action_at: 'Compromisso alterado',
   assigned_user_id: 'Responsável alterado',
};

// Etiqueta da origem do evento na timeline (§5.6 do guia).
export const ACTOR_TAGS = {
   user: 'Usuário',
   webhook: 'Asaas',
   job: 'Automático',
   system: 'Sistema',
};

export const STAGE_TYPE_LABELS = {
   ENTRY: 'Entrada',
   ATTEMPT: 'Tentativa',
   TERMINAL_PAID: 'Automática',
   EXTERNAL: 'Externa',
};

export const STAGE_TYPE_PILL_VARIANTS = {
   ENTRY: 'info',
   ATTEMPT: 'neutral',
   TERMINAL_PAID: 'success',
   EXTERNAL: 'critical',
};

// Explicação da mecânica de cada raia no modal de gestão (UI_SPEC §Raias).
export const STAGE_TYPE_HINTS = {
   ENTRY: 'Casos entram aqui pelo webhook de vencimento',
   TERMINAL_PAID: 'Baixa automática quando todos os títulos são pagos',
   EXTERNAL: 'Setor externo de cobrança, inicia o ciclo de cobrança terceirizado',
};

// ─── Trilha do caso (handoff 0103 — 2026-08-28) ──────────────────────────
// INTERNAL = régua da casa; EXTERNAL = escritório de cobrança administrativa.
// O mesmo cliente pode ter um caso aberto em CADA trilha — a chave do card é o
// `id` do caso, nunca o `customer_doc`.
//
// A trilha é atributo do CASO e não se confunde com a raia: o caso externo vive
// sozinho na raia EXTERNAL, mas quem manda nas regras (não arrasta, não muda de
// raia, aceita conferência) é o `track`.
export const TRACK_LABELS = {
   INTERNAL: 'Régua interna',
   EXTERNAL: 'Cobrança administrativa',
};

// ESPELHA o limite do backend (0103 §5.1: `charge_ids` tem 1..200). Validar
// aqui é UX; o backend revalida. O teto da nota de conferência mora no modal
// compartilhado (`pages/Finance/components/CheckChargeModal`).
export const TRANSFER_MAX_CHARGES = 200;

// As abas NÃO cobrem `TRANSFERRED` por decisão do Caio (2026-08-30): estado de
// controle do título, não recorte de trabalho. O caso estacionado não aparece
// em nenhuma aba — mas a tela ainda RENDERIZA um, porque escalar todos os
// títulos com o drawer aberto esvazia o caso de origem naquele instante (e o
// link de contraparte da timeline leva até ele). Quem trata isso é
// `caseFigures`/`commitPill`/`commitHeader`, não um filtro.
//
// O estado também se desfaz sozinho (D21): o caso volta a `OPEN` quando um
// título vence ou retorna, reaparecendo em "Em cobrança" sem ação de ninguém.
export const STATUS_TABS = [
   { value: 'OPEN', label: 'Em cobrança' },
   { value: 'SETTLED', label: 'Recebidos' },
   { value: 'CANCELLED', label: 'Cancelados' },
];

// Rótulos dos KPIs do topo por aba de status (handoff 0102 — 2026-08-24). A
// lista vem filtrada por status do servidor, então os três tiles falam sempre
// do mesmo bloco de números que os cards (`caseFigures`). Antes eram fixos em
// "em aberto" e zeravam nas abas de caso encerrado.
//
// O terceiro tile muda de natureza: em cobrança é a fila de ação imediata
// (`action_status === 'PENDING'`, em vermelho); encerrado é a contagem de
// títulos do bloco.
export const STATUS_KPI_LABELS = {
   OPEN: {
      cases: 'Casos em cobrança',
      amount: 'Em aberto',
      third: 'Ação agora',
   },
   SETTLED: {
      cases: 'Casos recebidos',
      amount: 'Total recebido',
      third: 'Títulos recebidos',
   },
   CANCELLED: {
      cases: 'Casos cancelados',
      amount: 'Valor cancelado',
      third: 'Títulos cancelados',
   },
};

// A visão "Linha do tempo" saiu do segmentado (decisão 2026-08-14): virou a
// tela gerencial própria em pages/Finance/DunningTimeline — visão geral, sem
// depender dos filtros operacionais desta tela.
export const VIEW_TABS = [
   { value: 'kanban', label: 'Kanban' },
   { value: 'fila', label: 'Fila' },
];

// Filtro de compromisso (decisão 2026-08-14). O backend não tem esse recorte
// (o contrato só expõe action_status PENDING/AWAITING) — é PÓS-FILTRO no
// cliente sobre a página carregada, derivado de `next_action_at`, com a mesma
// limitação documentada do "Sem responsável" (vale para a página corrente).
// "Sem compromisso" é categoria própria: é mais urgente que "atrasado" e
// misturá-los esconderia a diferença.
export const COMMIT_FILTER_OPTIONS = [
   { value: 'all', label: 'Todos os compromissos' },
   { value: 'ontrack', label: 'Em dia' },
   { value: 'today', label: 'Para hoje' },
   { value: 'late', label: 'Atrasados' },
   { value: 'none', label: 'Sem compromisso' },
];

// ─── Anexos da tratativa (handoff 0096) ──────────────────────────────────
// ESPELHAM os limites do backend. Validar no cliente é UX — o backend revalida
// e responde 422 (`invalid_file_type` / `upload_limit`). Não afrouxar nada aqui
// sem o backend mudar antes: limite do front mais largo = erro só no envio.
//
// O upload acontece SÓ na criação da tratativa (decisão 2026-08-19, handoff
// 0096 §5.1: tratativa + evidência nascem juntas e imutáveis). Não existe
// endpoint para anexar depois — a UI precisa dizer isso ANTES do envio.
export const ATTACHMENT_MAX_FILES = 3;
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_MIMES = [
   'application/pdf',
   'image/jpeg',
   'image/png',
   'image/webp',
];
export const ATTACHMENT_ACCEPT = ATTACHMENT_MIMES.join(',');
export const ATTACHMENT_MIMES_LABEL = 'PDF, JPEG, PNG ou WEBP';

// Motivo da exclusão do anexo (3–255 no backend).
export const ATTACHMENT_REASON_MIN = 3;
export const ATTACHMENT_REASON_MAX = 255;

// Uma página cobre o volume atual (92 casos no backfill). Se `total_pages > 1`
// a tela avisa no rodapé em vez de paginar por coluna (guia §3) — e os KPIs
// somados no cliente passam a valer só para a página corrente (lacuna §8.1).
export const LIST_PER_PAGE = 100;

// Timeline: uma página generosa de eventos; casos com mais que isso mostram os
// 100 mais recentes (auditoria completa fica para uma visão futura).
export const EVENTS_PER_PAGE = 100;

// Fila: paginação de EXIBIÇÃO (client-side, sobre a resposta já carregada) —
// limita a altura da tabela sem novo request por página.
export const QUEUE_PAGE_SIZE = 15;

// Ordenação da Fila — ESPELHA a whitelist de `order_by` do backend (handoffs
// 0091 e 0102; navegacao.md §1.10). Chave = valor do param; label alimenta o
// subtítulo ("ordenado por <label>"). Não adicionar coluna aqui sem o backend
// suportar antes.
export const QUEUE_SORTABLE_COLUMNS = {
   customer_name: 'cliente',
   interactions_count: 'tratativas',
   open_count: 'títulos',
   open_amount: 'valor em aberto',
   oldest_due_date: 'atraso',
   // 0102 §4: existe para a aba Recebidos — ordenar caso recebido por
   // `open_amount` é ruído (é sempre 0).
   settled_amount: 'valor recebido',
};

// Quais colunas da Fila são ordenáveis EM CADA ABA. A whitelist acima diz o que
// o backend aceita; esta diz o que faz sentido no recorte — ordenar por um
// campo que zerou não é ordenação, é ruído (mesma família do "filtro fantasma",
// navegacao.md §1.0.3). Sem entrada = coluna vira cabeçalho comum.
export const STATUS_SORTABLE_KEYS = {
   OPEN: new Set([
      'customer_name',
      'interactions_count',
      'open_count',
      'open_amount',
      'oldest_due_date',
   ]),
   // Não há `settled_count`/`cancelled_*` na whitelist do backend — as colunas
   // de contagem e o valor cancelado ficam sem ordenação de propósito.
   SETTLED: new Set(['customer_name', 'interactions_count', 'settled_amount']),
   CANCELLED: new Set(['customer_name', 'interactions_count']),
};
